import { ofType } from 'redux-observable'
import { Observable, of } from 'rxjs'

import System from 'system'

import {
  catchError,
  concatMap,
  debounceTime,
  filter as filterOperator,
  mergeMap,
  takeUntil,
  tap,
  throttleTime,
} from 'rxjs/operators'

import epicErrorHandler from '../actionsHandlers/epicErrorHandler'
import epicResponseHandler from '../actionsHandlers/epicResponseHandler'
import epicServerRequest from '../actionsHandlers/epicServerRequest'
import serverResponse from '../serverResponse'

import type { ActionCreatorWithPayload, AnyAction } from '@reduxjs/toolkit'
import type { Epic, StateObservable } from 'redux-observable'
import type { OperatorFunction } from 'rxjs'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TAny = any

type CustomAction = ActionCreatorWithPayload<TAny> | string
type SingleActionType<TAction> = TAction extends ActionCreatorWithPayload<TAny> ? ReturnType<TAction> : AnyAction
type ActionType<TAction> = TAction extends CustomAction[]
  ? SingleActionType<TAction[number]>
  : SingleActionType<TAction>

type RequestProps<TAction> =
  | {
      body?: object | URLSearchParams
      formEncode?: boolean

      headers?: Record<string, unknown>
      method: 'delete' | 'get' | 'post' | 'put'
      url: string
    }
  | Observable<TAction>
  | string
  | undefined

const epicRequest =
  <
    TActionType extends CustomAction | CustomAction[],
    TFulfilledActionType extends ActionCreatorWithPayload<TAny>,
    TFulfilledPayload = TFulfilledActionType extends ActionCreatorWithPayload<infer P> ? P : undefined,
    TFulfilledAction = TFulfilledActionType extends ActionCreatorWithPayload<infer P, infer T>
      ? { payload: Exclude<P, null | undefined>; type: T }
      : AnyAction,
    TErrorAction extends AnyAction = AnyAction,
    TAction = ActionType<TActionType>,
  >({
    actionType,
    cacheMS = 0,
    debounce = 0,
    errorAction,
    filter = undefined,
    fulfilledAction,
    maps = [],
    onComplete = () => null,
    operators = [],
    overlap = false,
    request,
    throttle = 0,
    untilTypes = [],
  }: {
    actionType: TActionType
    cacheMS?: number
    debounce?: number
    errorAction: TErrorAction
    filter?: (action: TAction, store$: StateObservable<GlobalState>) => boolean
    fulfilledAction: TFulfilledActionType
    maps?: ((response: unknown, action: TAction, store$: StateObservable<GlobalState>) => TAction)[]
    onComplete?: (action: TFulfilledAction) => void
    operators?: OperatorFunction<unknown, unknown>[]
    overlap?: boolean
    request?:
      | ((action: TAction, state$: StateObservable<GlobalState>) => Observable<TAny>)
      | ((action: TAction, state$: StateObservable<GlobalState>) => RequestProps<TAction>)
      | RequestProps<TAction>
    throttle?: number
    untilTypes?: CustomAction | CustomAction[]
  }): Epic =>
  (action$, store$: StateObservable<GlobalState>) => {
    const actionTypeArray: CustomAction[] = Array.isArray(actionType) ? actionType : [actionType]
    const [type, ...types] = actionTypeArray.map((act) => (typeof act === 'string' ? act : act.type))
    const injectStore =
      (callback: (action: TAction, store$: StateObservable<GlobalState>) => boolean) => (action: TAction) =>
        callback(action, store$)

    return action$.pipe(
      ofType(type, ...types),
      // map of observables are tricky to type
      // @ts-expect-error types not match
      ...[
        filter && filterOperator(injectStore(filter)),
        debounce > 0 && debounceTime(debounce),
        throttle > 0 && throttleTime(throttle),
        // @ts-expect-error types not match
        ...operators.map(injectStore),
      ].filter(Boolean),
      mergeMap((action: TAction) => {
        let params: RequestProps<TAction> = typeof request === 'function' ? request(action, store$) : request
        const [untilAction, ...untilActions] = [
          ...(overlap ? [] : [type, ...types]),
          ...(Array.isArray(untilTypes) ? untilTypes : [untilTypes]).map((until) =>
            typeof until === 'string' ? until : until.type,
          ),
        ].filter(Boolean)

        let stream: Observable<TAny>
        let cacheKey = ''
        let cacheSave = false

        if (params instanceof Observable) {
          stream = params
        } else if (params) {
          if (typeof params === 'string') {
            params = { method: 'get', url: params }
          }

          if (cacheMS > 0 && window.localStorage && (!params.method || params.method === 'get')) {
            cacheKey = `cache_${params.url}`
            const cacheString = window.localStorage.getItem(cacheKey)
            if (cacheString) {
              let cache = { content: {}, timeout: 0 }
              try {
                cache = JSON.parse(cacheString) as typeof cache
              } catch (e: unknown) {
                // eslint-disable-next-line no-console
                console.error(e)
              }

              if (cache?.timeout > Date.now()) {
                stream = of({ code: 200, response: cache.content })
              } else {
                cacheSave = true
              }
            } else {
              cacheSave = true
            }
          }

          // @ts-expect-error types not match
          if (!stream) {
            let { body } = params
            if (params.formEncode) {
              params.headers = Object.assign(params.headers || {}, {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
              })
              body = Object.entries(params.body || {}).reduce((acc, entry) => {
                acc.append(...entry)
                return acc
              }, new URLSearchParams())
            }

            stream = epicServerRequest(
              params.method || 'get',
              params.url.match(/^http/i) ? params.url || params : `${System.api_url}/${params.url || params}`,
              action,
              body,
              params.headers,
            )
          }
        } else {
          stream = of({ code: 200, response: serverResponse() })
        }

        return stream.pipe(
          concatMap((response: TFulfilledPayload) =>
            of(
              ...[
                // @ts-expect-error types not match
                epicResponseHandler(fulfilledAction, action)(response),
                ...maps.map((act) => act(response, action, store$)),
              ].filter(Boolean),
            ),
          ),
          tap(({ payload }) => {
            if (cacheSave && cacheKey) {
              try {
                window.localStorage.setItem(
                  cacheKey,
                  JSON.stringify({ content: payload, timeout: Date.now() + cacheMS }),
                  //
                )
              } catch (error: unknown) {
                // eslint-disable-next-line no-console
                console.error(error)
              }
            }
            return payload
          }),
          tap(onComplete),
          takeUntil(action$.pipe(ofType(untilAction, ...untilActions))),
          // @ts-expect-error types not match
          catchError(epicErrorHandler(errorAction, action)),
        )
      }),
    )
  }

export default epicRequest
